import React from "react";
import immutabilityUpdate from "immutability-helper";
import { cloneDeep, isPlainObject } from "lodash";

import { Resolve, update } from "@jsonforms/core";
import { JsonFormsStateContext } from "@jsonforms/react";

export type PlainObject = Record<string, unknown>;

export const moveToTop = ({
	currentIndex,
	path,
	ctx,
	setAnnouncement,
}: {
	currentIndex: number;
	path: string;
	ctx: JsonFormsStateContext;
	setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
}) => {
	if (currentIndex === 0) return () => {};

	return () => {
		if (!ctx.core?.data || !ctx.dispatch) return;
		const arrayData = Resolve.data(ctx.core?.data, path);
		if (!Array.isArray(arrayData)) return;

		const newArray = [...arrayData];
		const [item] = newArray.splice(currentIndex, 1);
		newArray.unshift(item);

		ctx.dispatch(update(path, () => newArray));
		setAnnouncement(`Moved item from position ${currentIndex + 1} to position 1`);
	};
};

export const moveToBottom = ({
	currentIndex,
	path,
	data,
	ctx,
	setAnnouncement,
}: {
	currentIndex: number;
	path: string;
	data: number;
	ctx: JsonFormsStateContext;
	setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
}) => {
	if (currentIndex === data - 1) return () => {};
	return () => {
		if (!ctx.core?.data || !ctx.dispatch) return;
		const arrayData = Resolve.data(ctx.core?.data, path);
		if (!Array.isArray(arrayData)) return;

		const newArray = [...arrayData];
		const [item] = newArray.splice(currentIndex, 1);
		newArray.push(item);

		ctx.dispatch(update(path, () => newArray));
		setAnnouncement(`Moved item from position ${currentIndex + 1} to position ${data}`);
	};
};

export const moveCard = ({
	dragIndex,
	hoverIndex,
	path,
	ctx,
}: {
	dragIndex: number;
	hoverIndex: number;
	path: string;
	ctx: JsonFormsStateContext;
}) => {
	if (dragIndex === hoverIndex) return;

	if (!ctx.core?.data || !ctx.dispatch) return;
	const arrayData = Resolve.data(ctx.core?.data, path);
	if (!Array.isArray(arrayData)) return;

	ctx.dispatch(
		update(path, () =>
			immutabilityUpdate(arrayData, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, arrayData[dragIndex]],
				],
			})
		)
	);
};

export type JsonSchema = {
	properties?: Record<string, any>;
	type?: string | string[];
	items?: JsonSchema;
	[key: string]: any;
};

export type MergeObjectsError = "missing-schema" | "type-mismatch";

export interface MergeObjectsResult {
	value: PlainObject | null;
	error: MergeObjectsError | null;
}

/**
 * Extract property keys from a JSON schema object
 */
const getSchemaProperties = (schema: any): string[] | null => {
	if (schema && typeof schema === "object" && "properties" in schema) {
		return Object.keys(schema.properties);
	}
	return null;
};

/**
 * Get nested schema for a specific property
 */
const getNestedSchema = (schema: JsonSchema | undefined, key: string): JsonSchema | undefined => {
	if (schema && typeof schema === "object" && "properties" in schema) {
		return (schema.properties as Record<string, JsonSchema>)?.[key];
	}
	return undefined;
};

const getSchemaTypes = (schema?: JsonSchema): string[] => {
	if (!schema?.type) {
		return [];
	}

	return Array.isArray(schema.type) ? schema.type : [schema.type];
};

const matchesSchemaType = (value: unknown, expectedType: string): boolean => {
	switch (expectedType) {
		case "string":
			return typeof value === "string";
		case "number":
			return typeof value === "number" && Number.isFinite(value);
		case "integer":
			return typeof value === "number" && Number.isInteger(value);
		case "boolean":
			return typeof value === "boolean";
		case "null":
			return value === null;
		case "array":
			return Array.isArray(value);
		case "object":
			return isPlainObject(value);
		default:
			return true;
	}
};

const validateValueAgainstSchema = (value: unknown, schema: JsonSchema): boolean => {
	const schemaTypes = getSchemaTypes(schema);

	if (schemaTypes.length > 0 && !schemaTypes.some((type) => matchesSchemaType(value, type))) {
		return false;
	}

	if (Array.isArray(value)) {
		if (!schema.items) {
			return true;
		}

		return value.every((item) => validateValueAgainstSchema(item, schema.items as JsonSchema));
	}

	if (isPlainObject(value)) {
		const schemaProps = getSchemaProperties(schema);
		if (!schemaProps) {
			return true;
		}

		return schemaProps.every((key) => {
			if (!Object.prototype.hasOwnProperty.call(value, key)) {
				return true;
			}

			const nestedSchema = getNestedSchema(schema, key);
			return nestedSchema ? validateValueAgainstSchema((value as PlainObject)[key], nestedSchema) : true;
		});
	}

	return true;
};

const filterValueBySchema = (
	value: unknown,
	schema: JsonSchema
): { value: unknown; error: MergeObjectsError | null } => {
	if (!validateValueAgainstSchema(value, schema)) {
		return {
			value: null,
			error: "type-mismatch",
		};
	}

	if (Array.isArray(value)) {
		if (!schema.items) {
			return {
				value: cloneDeep(value),
				error: null,
			};
		}

		const filteredItems: unknown[] = [];

		for (const item of value) {
			const filteredItem = filterValueBySchema(item, schema.items as JsonSchema);
			if (filteredItem.error) {
				return filteredItem;
			}

			filteredItems.push(filteredItem.value);
		}

		return {
			value: filteredItems,
			error: null,
		};
	}

	if (isPlainObject(value)) {
		const schemaProps = getSchemaProperties(schema);
		if (!schemaProps) {
			return {
				value: cloneDeep(value),
				error: null,
			};
		}

		const result: PlainObject = {};

		for (const key of schemaProps) {
			if (!Object.prototype.hasOwnProperty.call(value, key)) {
				continue;
			}

			const sourceValue = (value as PlainObject)[key];
			const nestedSchema = getNestedSchema(schema, key);

			if (!nestedSchema) {
				result[key] = cloneDeep(sourceValue);
				continue;
			}

			const filteredValue = filterValueBySchema(sourceValue, nestedSchema);
			if (filteredValue.error) {
				return filteredValue;
			}

			result[key] = filteredValue.value;
		}

		return {
			value: result,
			error: null,
		};
	}

	return {
		value: cloneDeep(value),
		error: null,
	};
};

/**
 * Filter source object to include only keys/paths defined in schema
 * Returns a filtered copy of source aligned with schema structure
 */
const filterSourceBySchema = (
	source: PlainObject,
	schema: JsonSchema | string[]
): MergeObjectsResult => {
	if (Array.isArray(schema)) {
		const result: PlainObject = {};

		for (const key of schema) {
			if (Object.prototype.hasOwnProperty.call(source, key)) {
				result[key] = cloneDeep(source[key]);
			}
		}

		return {
			value: result,
			error: null,
		};
	}

	let elementSchema: JsonSchema | string[] | undefined = schema;

	// If schema is an array schema, extract the items schema (element schema)
	if (!Array.isArray(schema) && schema && typeof schema === "object") {
		const schemaObj = schema as any;
		if (schemaObj.type === "array" && schemaObj.items) {
			elementSchema = schemaObj.items;
		}
	}

	if (!elementSchema || Array.isArray(elementSchema)) {
		return {
			value: null,
			error: "type-mismatch",
		};
	}

	return filterValueBySchema(source, elementSchema as JsonSchema) as MergeObjectsResult;
};

export const mergeObjects = (
	source: PlainObject,
	schema?: JsonSchema | string[]
): MergeObjectsResult => {
	if (!schema) {
		return {
			value: null,
			error: "missing-schema",
		};
	}

	return filterSourceBySchema(source, schema);
};
